import classNames from "classnames";
import { IFImg } from "@libs/utils/IFImg";
import { LoadingSpinner } from "../LoadingSpinner";
import styles from "./index.module.css";
import type { MouseEventHandler } from "react";
import type { Settings, CustomArrowProps } from "react-slick";

type ShouldInterceptNextClickParams = Pick<
  CustomArrowProps,
  "currentSlide" | "slideCount"
>;

type NextArrowProps = CustomArrowProps & {
  canLoadMore?: boolean;
  isLoading?: boolean;
  onInterceptNextClick?: (
    params: ShouldInterceptNextClickParams
  ) => Promise<boolean>;
};

const PrevArrow: React.FC<CustomArrowProps> = ({ onClick, currentSlide }) => {
  const disabled = typeof currentSlide === "undefined" || currentSlide === 0;

  return (
    <div
      className={classNames(styles.arrow, styles.prev, {
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
    >
      <img
        src={IFImg("/img/icon/arrow_right.svg")}
        width={16}
        height={20}
        alt=""
      />
    </div>
  );
};

const NextArrow: React.FC<NextArrowProps> = ({
  onClick,
  currentSlide,
  slideCount,
  canLoadMore,
  isLoading,
  onInterceptNextClick,
}) => {
  const isLastVisibleSlide =
    typeof currentSlide !== "undefined" &&
    typeof slideCount !== "undefined" &&
    currentSlide === slideCount - 1;
  const disabled =
    typeof currentSlide === "undefined" ||
    typeof slideCount === "undefined" ||
    (isLastVisibleSlide && !canLoadMore) ||
    isLoading;
  const handleClick: MouseEventHandler<HTMLDivElement> = async (event) => {
    if (onInterceptNextClick) {
      const intercepted = await onInterceptNextClick({
        currentSlide,
        slideCount,
      });

      if (intercepted) {
        return;
      }
    }

    onClick && onClick(event);
  };

  return (
    <div
      className={classNames(styles.arrow, styles.next, {
        [styles.disabled]: disabled,
      })}
      onClick={handleClick}
    >
      {isLoading ? (
        <LoadingSpinner className={styles.loadingSpinner} />
      ) : (
        <img
          src={IFImg("/img/icon/arrow_right.svg")}
          width={16}
          height={20}
          alt=""
        />
      )}
    </div>
  );
};

type CreateSlideSettingProps = {
  canLoadMore?: boolean;
  isLoading?: boolean;
  onInterceptNextClick?: (
    params: ShouldInterceptNextClickParams
  ) => Promise<boolean>;
};

export const createSlideSetting = ({
  canLoadMore,
  isLoading,
  onInterceptNextClick,
}: CreateSlideSettingProps = {}): Settings => ({
  dots: false,
  infinite: false,
  arrows: true,
  prevArrow: <PrevArrow />,
  nextArrow: (
    <NextArrow
      canLoadMore={canLoadMore}
      isLoading={isLoading}
      onInterceptNextClick={onInterceptNextClick}
    />
  ),
  responsive: [
    {
      breakpoint: 813,
      settings: "unslick",
    },
  ],
});
